import { useState, useRef, useEffect, useCallback } from 'react';
import { useAutoSave } from '@/hooks';
import { TrashIcon, Cog6ToothIcon, EllipsisVerticalIcon, XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useConfirmDialog } from '../../../hooks/useConfirmDialog';
import { ConfirmDialog } from '../../../components/shared/UIComponents';
import disciplineConfig from '../../../services/disciplineConfigService';
import EditableField from '../../../components/shared/EditableField';
import OffenceCard from '../../../components/discipline/OffenceCard';
import { useSchool } from '@/contexts/SchoolContext';
import PeriodUndefinedBadge from './PeriodUndefinedBadge';

function AccountabilityRuleForm({ rule = null, ruleId = null, categoryKey = null, onSave, onDelete, availableOffences = [], isRecognitionMode = false }) {
  const { confirmDialog, showConfirm } = useConfirmDialog();
  const { schoolId } = useSchool();
  const prevRuleId = useRef(rule?.id);

  // Initialize with proper defaults
  const [data, setData] = useState(() => {
    const defaults = {
      title: isRecognitionMode ? 'Momentum Rule' : 'Accountability Rule',
      triggerOffenses: [],
      triggerCount: 3,
      triggerTimeframe: 'month',
      periodLength: 7,
      extensionLength: 7,
      maxTotalPeriod: 21,
      duringConsequence: 'detention',
      duringConsequenceId: null,
      allowExtensions: true,
      quickRetrigger: true,
      quickRetriggerPeriod: 7,
      active: true
    };

    return rule ? { ...defaults, ...rule, duringConsequenceId: rule.duringConsequenceId || null } : defaults;
  });

  const [showConfig, setShowConfig] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [showOffenseSelector, setShowOffenseSelector] = useState(false);
  const [consequenceTypes, setConsequenceTypes] = useState([]);
  const [timeframeOptions, setTimeframeOptions] = useState([]);

  const menuRef = useRef();
  const offenseSelectorRef = useRef();

  // Load consequence types from Firestore
  useEffect(() => {
    const loadConsequenceTypes = async () => {
      try {
        const types = await disciplineConfig.getConsequenceTypeOptions(schoolId);
        setConsequenceTypes(types.map(c => ({
          value: c.value,
          label: c.singular,
          id: c.id
        })));
      } catch (error) {
        console.error('Error loading consequence types:', error);
      }
    };
    loadConsequenceTypes();
  }, [schoolId]);

  // Load timeframes from Firestore
  useEffect(() => {
    const loadTimeframes = async () => {
      try {
        const options = await disciplineConfig.getTimeframeOptions(schoolId);
        setTimeframeOptions(options);
      } catch (error) {
        console.error('Error loading timeframes:', error);
      }
    };
    loadTimeframes();
  }, [schoolId]);

  // Update data when we get a different rule
  useEffect(() => {
    if (rule?.id && rule.id !== prevRuleId.current) {
      prevRuleId.current = rule.id;
      setData({
        title: rule.title || (isRecognitionMode ? 'Momentum Rule' : 'Accountability Rule'),
        triggerOffenses: rule.triggerOffenses || [],
        triggerCount: rule.triggerCount || 3,
        triggerTimeframe: rule.triggerTimeframe || 'month',
        periodLength: rule.periodLength || 7,
        extensionLength: rule.extensionLength || 7,
        maxTotalPeriod: rule.maxTotalPeriod || 21,
        duringConsequence: rule.duringConsequence || 'detention',
        duringConsequenceId: rule.duringConsequenceId || null,
        allowExtensions: rule.allowExtensions !== false,
        quickRetrigger: rule.quickRetrigger !== false,
        quickRetriggerPeriod: rule.quickRetriggerPeriod || 7,
        active: rule.active !== false,
        ...(rule.id && { id: rule.id })
      });
    }
  }, [rule]);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
      if (offenseSelectorRef.current && !offenseSelectorRef.current.contains(event.target)) {
        setShowOffenseSelector(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { save: debouncedSave } = useAutoSave(onSave, 100);

  const update = useCallback((updates) => {
    let newData;
    setData(current => {
      newData = { ...current, ...updates };

      // If periodLength is being updated, sync the other period-related fields
      if (updates.periodLength !== undefined) {
        newData.extensionLength = updates.periodLength;
        newData.quickRetriggerPeriod = updates.periodLength;
      }

      // If updating duringConsequence, also update the corresponding ID field
      if (updates.duringConsequence) {
        const typeOption = consequenceTypes.find(opt => opt.value === updates.duringConsequence);
        if (typeOption) {
          newData.duringConsequenceId = typeOption.id;
        }
      }

      return newData;
    });

    const actualRuleId = ruleId || rule?.id || newData.id;
    debouncedSave(newData, categoryKey || 'accountability', actualRuleId);
  }, [debouncedSave, categoryKey, ruleId, rule?.id, consequenceTypes]);

  // Sortable setup
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rule?.id || 'temp' });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  // Get offense objects from IDs
  const getSelectedOffenses = () => {
    return data.triggerOffenses.map(id => {
      const offense = availableOffences.find(o => o.id === id || o.name === id);
      return { ...(offense || { id, name: id }), reference: id };
    }).filter(Boolean);
  };

  // Add offense to the list
  const addOffense = (offense) => {
    if (!data.triggerOffenses.includes(offense.id)) {
      update({ triggerOffenses: [...data.triggerOffenses, offense.id] });
    }
    setShowOffenseSelector(false);
  };

  // Remove offense from the list
  const removeOffense = (offenseId) => {
    update({ triggerOffenses: data.triggerOffenses.filter(id => id !== offenseId) });
  };

  // Filter out already selected offenses
  const availableOffensesFiltered = availableOffences.filter(
    offense => !data.triggerOffenses.some(reference => reference === offense.id || reference === offense.name)
  );

  const getOffenseText = () => {
    const offenses = getSelectedOffenses();
    const anyText = isRecognitionMode ? 'any achievement' : 'any offense';
    if (offenses.length === 0) return anyText;
    if (offenses.length === 1) return offenses[0].name;
    return `${offenses.length} selected ${isRecognitionMode ? 'achievements' : 'offenses'}`;
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-lg shadow-sm ${!isDragging ? 'hover:shadow-md' : ''} relative transition-shadow`}
    >
      <div className="flex items-center p-4">
        {/* Drag Handle */}
        <div
          className="mr-2 py-1 cursor-grab hover:bg-base-200 rounded focus:outline-none"
          {...attributes}
          {...listeners}
          tabIndex={-1}
        >
          <svg
            className="w-4 h-4 text-base-content/50"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <circle cx="7" cy="5" r="1.5" />
            <circle cx="7" cy="10" r="1.5" />
            <circle cx="7" cy="15" r="1.5" />
            <circle cx="13" cy="5" r="1.5" />
            <circle cx="13" cy="10" r="1.5" />
            <circle cx="13" cy="15" r="1.5" />
          </svg>
        </div>

        <div className="flex-1">
          {/* Editable Title */}
          <div className="mb-3">
            <input
              type="text"
              defaultValue={data.title}
              onBlur={(e) => {
                const newTitle = e.target.value.trim() || (isRecognitionMode ? 'Momentum Rule' : 'Accountability Rule');
                if (newTitle !== data.title) {
                  update({ title: newTitle });
                }
                setEditingTitle(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.target.blur();
                }
              }}
              className={`text-lg font-medium bg-transparent border-0 p-0 w-full outline-none cursor-pointer ${
                editingTitle ? 'text-black' : 'text-blue-600 hover:text-blue-700'
              }`}
              onFocus={() => setEditingTitle(true)}
            />
          </div>

          {/* Main sentence with controls positioned relative to this container */}
          <div className="flex items-start justify-between">
            {/* Main Rule Description */}
            <div className="text-base flex-1">
              <div className="mb-2">
                When a student {isRecognitionMode ? 'earns' : 'gets'}{' '}
                <EditableField
                  field="triggerCount"
                  value={data.triggerCount}
                  type="number"
                  onUpdate={update}
                  editingField={editingField}
                  setEditingField={setEditingField}
                  validation={{ min: 1, max: 99 }}
                />
                {' '}{isRecognitionMode ? 'recognitions for' : 'mentions for'}{' '}
                <span
                  className="font-medium text-blue-600 px-1 -mx-0.5 rounded transition-colors cursor-pointer hover:bg-base-200"
                  onClick={() => setShowConfig(!showConfig)}
                  title="Click to configure offenses"
                >
                  {getOffenseText()}
                </span>
                {' '}in a{' '}
                <EditableField
                  field="triggerTimeframe"
                  value={data.triggerTimeframe}
                  options={timeframeOptions.map(opt => ({
                    value: opt.value,
                    label: opt.singular
                  }))}
                  onUpdate={update}
                  editingField={editingField}
                  setEditingField={setEditingField}
                />
                <PeriodUndefinedBadge timeframe={data.triggerTimeframe} />
                , they enter a{' '}
                <EditableField
                  field="periodLength"
                  value={data.periodLength}
                  type="number"
                  onUpdate={update}
                  editingField={editingField}
                  setEditingField={setEditingField}
                  validation={{ min: 1, max: 99 }}
                />
                {' '}day {isRecognitionMode ? 'momentum' : 'accountability'} period.
              </div>

              <div className="mb-2 ml-4">
                {isRecognitionMode ? 'During momentum: new recognition' : 'During accountability: new mention'} →{' '}
                <EditableField
                  field="duringConsequence"
                  value={data.duringConsequence}
                  options={consequenceTypes}
                  onUpdate={update}
                  editingField={editingField}
                  setEditingField={setEditingField}
                />
              </div>

            </div>

            {/* Right Side Controls - positioned relative to main sentence */}
            <div className="flex items-center gap-2 ml-4">
              {/* Active Toggle */}
              <input
                type="checkbox"
                className="toggle toggle-sm toggle-success"
                checked={data.active}
                onChange={(e) => update({ active: e.target.checked })}
                title={data.active ? 'Rule is active' : 'Rule is inactive'}
              />

              {/* Three dots menu */}
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="p-1.5 hover:bg-base-200 rounded"
                >
                  <EllipsisVerticalIcon className="w-4 h-4" />
                </button>

                <AnimatePresence>
                  {showMenu && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -10 }}
                      className="absolute right-0 mt-1 w-40 bg-white border border-base-200 rounded-lg shadow-lg z-10 overflow-hidden"
                    >
                      <button
                        onClick={() => {
                          setShowConfig(!showConfig);
                          setShowMenu(false);
                        }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-base-100 flex items-center gap-2"
                      >
                        <Cog6ToothIcon className="w-4 h-4" />
                        {showConfig ? 'Less options' : 'More options'}
                      </button>
                      {onDelete && (
                        <button
                          onClick={async () => {
                            setShowMenu(false);
                            const confirmed = await showConfirm({
                              title: 'Delete Rule?',
                              message: 'Delete this rule permanently? This action cannot be undone.',
                              variant: 'danger',
                              confirmText: 'Delete',
                              cancelText: 'Cancel'
                            });
                            if (confirmed) {
                              const deleteId = ruleId || rule?.id || data.id;
                              if (deleteId) {
                                await onDelete(deleteId);
                              }
                            }
                          }}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-error/10 text-error flex items-center gap-2"
                        >
                          <TrashIcon className="w-4 h-4" />
                          Delete
                        </button>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {showConfig && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.15, ease: "easeInOut" }}
                style={{ overflow: 'hidden' }}
              >
                <div className="mt-4 pt-4 border-t border-base-200 space-y-4"
                >
              {/* Offense Selection */}
              <div>
                <label className="text-sm font-medium text-base-content/70 mb-3 block">{isRecognitionMode ? 'TRIGGER ACHIEVEMENTS' : 'TRIGGER OFFENSES'}</label>
                <div className="pt-4 px-4 pb-2 bg-base-50 border border-base-200 rounded-lg min-h-[100px] flex flex-col">
                  {/* Selected offense cards */}
                  <div className="flex flex-wrap gap-2 flex-1 mb-3">
                    {getSelectedOffenses().map((offense) => (
                      <div key={offense.id} className="relative group">
                        <OffenceCard
                          offence={offense}
                          variant="button"
                          showLevel={false}
                          className="relative"
                        />
                        <button
                          onClick={() => removeOffense(offense.reference)}
                          className="absolute -top-1 -right-1 bg-base-200 text-base-content rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-base-300 shadow-sm"
                        >
                          <XMarkIcon className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Add offense button */}
                  <div className="flex justify-center">
                    <div className="relative" ref={offenseSelectorRef}>
                      <button
                        onClick={() => setShowOffenseSelector(!showOffenseSelector)}
                        className="px-3 py-2 text-base-content/60 rounded-lg text-sm hover:bg-base-100 hover:text-base-content transition-colors flex items-center gap-1.5"
                      >
                        <PlusIcon className="w-4 h-4" />
                        {isRecognitionMode ? 'Add achievement' : 'Add offense'}
                      </button>

                      {/* Offense selector dropdown */}
                      <AnimatePresence>
                        {showOffenseSelector && availableOffensesFiltered.length > 0 && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: -10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: -10 }}
                            className="absolute left-1/2 transform -translate-x-1/2 mt-1 w-80 max-h-64 overflow-y-auto bg-white border border-base-200 rounded-lg shadow-lg z-10"
                          >
                            <div className="p-2 space-y-1">
                              {availableOffensesFiltered.map(offense => (
                                <OffenceCard
                                  key={offense.id}
                                  offence={offense}
                                  variant="list"
                                  showLevel={true}
                                  showDescription={true}
                                  clickable={true}
                                  onClick={() => addOffense(offense)}
                                  className="hover:bg-base-50"
                                />
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </div>
              </div>

              {/* Extension Option */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm"
                  checked={data.allowExtensions}
                  onChange={(e) => update({ allowExtensions: e.target.checked })}
                />
                <span className="text-sm">Each {isRecognitionMode ? 'recognition during momentum' : 'offense during accountability'} extends period by{' '}
                  <EditableField
                    field="extensionLength"
                    value={data.extensionLength}
                    type="number"
                    onUpdate={update}
                    editingField={editingField}
                    setEditingField={setEditingField}
                    validation={{ min: 1, max: 99 }}
                    disabled={!data.allowExtensions}
                  />
                  {' '}days (max{' '}
                  <EditableField
                    field="maxTotalPeriod"
                    value={data.maxTotalPeriod}
                    type="number"
                    onUpdate={update}
                    editingField={editingField}
                    setEditingField={setEditingField}
                    validation={{ min: 1, max: 99 }}
                    disabled={!data.allowExtensions}
                  />
                  {' '}days total)
                </span>
              </div>

              {/* Quick Re-trigger Option */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm"
                  checked={data.quickRetrigger}
                  onChange={(e) => update({ quickRetrigger: e.target.checked })}
                />
                <span className="text-sm">After exiting, next {isRecognitionMode ? 'recognition' : 'offense'} triggers a new{' '}
                  <EditableField
                    field="quickRetriggerPeriod"
                    value={data.quickRetriggerPeriod}
                    type="number"
                    onUpdate={update}
                    editingField={editingField}
                    setEditingField={setEditingField}
                    validation={{ min: 1, max: 99 }}
                    disabled={!data.quickRetrigger}
                  />
                  {' '}day {isRecognitionMode ? 'momentum' : 'accountability'} period
                </span>
              </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog {...confirmDialog} />
    </div>
  );
}

export default AccountabilityRuleForm;
